// Copyright (c) 2023 Ryan Piaskowy. All Rights Reserved.
import React from "react";
import EventImage from "./EventImage";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const INLINE_EVENT_LIMIT = 2;

// Returns midnight (Central Time) for "today", used as the anchor for the grid.
function getCentralToday() {
  const now = new Date();
  const centralString = now.toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
  });
  return new Date(`${centralString} 00:00:00`);
}

function toDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Builds an 8-week grid starting on the Sunday of the current week.
function buildDays(weeksToShow) {
  const today = getCentralToday();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());

  const days = [];
  for (let i = 0; i < weeksToShow * 7; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    days.push(date);
  }
  return { days, today };
}

function WeekCalendar({ eventData, onShowDayEvents, onViewImage, weeksToShow }) {
  const { days, today } = buildDays(weeksToShow);

  return (
    <div className="week-calendar">
      <div className="week-calendar__weekdays">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="week-calendar__weekday">
            {label}
          </div>
        ))}
      </div>
      <div className="week-calendar__grid">
        {days.map((date) => {
          const dateString = toDateString(date);
          const dayEvents = eventData.filter(({ eventDates }) =>
            eventDates.includes(dateString)
          );
          const isToday = dateString === toDateString(today);

          return (
            <div
              key={dateString}
              className={`week-calendar__day ${isToday ? "is-today" : ""}`}
            >
              <button
                type="button"
                className="week-calendar__day-header"
                onClick={() => dayEvents.length > 0 && onShowDayEvents(date, dayEvents)}
                disabled={dayEvents.length === 0}
                aria-label={`${date.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}${dayEvents.length ? `, ${dayEvents.length} events` : ", no events"}`}
              >
                <span className="week-calendar__day-number">{date.getDate()}</span>
                {dayEvents.length > 0 && (
                  <span className="week-calendar__day-count">
                    {dayEvents.length} event{dayEvents.length === 1 ? "" : "s"}
                  </span>
                )}
              </button>

              {dayEvents.length > 0 && dayEvents.length <= INLINE_EVENT_LIMIT && (
                <div className="week-calendar__day-inline">
                  {dayEvents.map((event, index) =>
                    event.imageUrl ? (
                      <EventImage
                        key={index}
                        event={event}
                        className="week-calendar__thumb"
                        onView={() => onViewImage([event], 0)}
                      />
                    ) : (
                      <div
                        key={index}
                        className="poster poster-inline"
                        onClick={() => onShowDayEvents(date, dayEvents)}
                      >
                        <div className="poster-content">
                          {`${event.eventTitle}: ${event.eventNotes}`.slice(0, 200)}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}

              {dayEvents.length > INLINE_EVENT_LIMIT && (
                <button
                  className="week-calendar__more-button"
                  onClick={() => onShowDayEvents(date, dayEvents)}
                >
                  View all {dayEvents.length}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WeekCalendar;
